import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import axios, { TOKEN_KEY } from "./utils/api";
import { AnimatePresence, motion } from "framer-motion";
import Landing   from "./Landing";
import AuthModal from "./AuthModal";
import ResetPassword from "./ResetPassword";

import Navbar          from "./components/Navbar";
import MarketBar       from "./components/MarketBar";
import AlertsBanner    from "./components/AlertsBanner";
import SettingsDrawer  from "./components/Modals/SettingsDrawer";
import StockDetailModal from "./components/Modals/StockDetailModal";
import ScannerTab      from "./components/Scanner/ScannerTab";
import WatchlistTab    from "./components/Watchlist/WatchlistTab";
import CompleteProfileGate from "./components/Auth/CompleteProfileGate";
import VerifyEmailGate from "./components/Auth/VerifyEmailGate";
import TrialEndedGate from "./components/Auth/TrialEndedGate";

import { useMarketStatus }      from "./hooks/useMarketStatus";
import { useAutoScan }          from "./hooks/useAutoScan";
import { useUserData }          from "./hooks/useUserData";
import { useSubscription }      from "./hooks/useSubscription";
import { usePricing }          from "./hooks/usePricing";

import { BG, BD, TX, GR, RD, YL } from "./utils/constants";
import { alertTargets } from "./utils/formatUtils";
import { trackEvent } from "./utils/api";

const tabVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.18, ease: "easeOut" } },
  exit:    { opacity: 0, y: -6, transition: { duration: 0.12 } },
};

export default function App() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authView,    setAuthView]    = useState(null);
  const [resetToken,  setResetToken]  = useState(() => new URLSearchParams(window.location.search).get("resetToken"));

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthLoading(false); return; }
    axios.get("/api/me")
      .then((res) => setUser(res.data.user))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setAuthLoading(false));
  }, []);

  const handleResetDone = useCallback((authUser) => {
    window.history.replaceState({}, "", window.location.pathname);
    setResetToken(null);
    setUser(authUser);
  }, []);

  // ── Scan state ────────────────────────────────────────────────────────────
  const [loading,    setLoading]    = useState(false);
  const [scanPhase,  setScanPhase]  = useState("");
  const [scanError,  setScanError]  = useState(null);
  // Real coverage from the last scan, so the header reports what was actually
  // scored rather than how many rows came back.
  const [coverage,   setCoverage]   = useState(null);

  // ── UI ────────────────────────────────────────────────────────────────────
  const [tab,            setTab]          = useState("scanner");
  const [sector,        setSector]        = useState("All");
  const [sortBy,        setSortBy]        = useState("score");
  const [search,        setSearch]        = useState("");
  const [settingsOpen,  setSettingsOpen]  = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newsMap,       setNewsMap]       = useState({});

  // ── Settings ──────────────────────────────────────────────────────────────
  const [topN, setTopN] = useState(5);

  // ── Alerts ────────────────────────────────────────────────────────────────
  const [alertInput,  setAlertInput]  = useState("");
  const [firedAlerts, setFiredAlerts] = useState([]);

  const newsCache  = useRef({});

  // ── Hooks ─────────────────────────────────────────────────────────────────
  const { marketOpen, marketData, fetchMarket } = useMarketStatus();
  const {
    watchlist, toggleWL,
    investments, addInvestment, removeInvestment, closeTrade, setInvestedAmount,
    predLog, updatePredictions,
    alerts, setAlerts,
    scannedStocks, setScannedStocks,
    lastScan, setLastScan,
    syncFailed, retrySync,
  } = useUserData();
  const scanned = scannedStocks.length > 0;
  const { subscription, isPro, loading: subLoading, daysLeftInTrial, refreshSubscription } = useSubscription(user);
  const pricing = usePricing();

  const handleSubscribe = useCallback(async () => {
    try {
      const res = await axios.post("/api/billing/checkout");
      // An admin doesn't need to pay; the server says so rather than charging.
      if (res.data?.adminBypass) { refreshSubscription(); return; }
      if (res.data?.url) { window.location.href = res.data.url; return; }
      window.alert("Could not start checkout — please try again.");
    } catch (e) {
      // 501 means the Stripe keys aren't set yet. That is a setup state, not a
      // fault of the user's, so it keeps the plain-English message rather than
      // surfacing a backend error.
      if (e?.response?.status === 501) {
        window.alert("Payments aren't set up yet — check back soon.");
        return;
      }
      window.alert(e?.response?.data?.error || "Could not start checkout — please try again.");
    }
  }, [refreshSubscription]);

  const handleManageBilling = useCallback(async () => {
    try {
      const res = await axios.post("/api/billing/portal");
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      window.alert(e?.response?.data?.error || "Could not open billing portal.");
    }
  }, []);

  // Coming back from Stripe Checkout — the webhook that actually activates the subscription can
  // land a moment after the redirect, so poll briefly instead of refreshing access just once.
  useEffect(() => {
    const billing = new URLSearchParams(window.location.search).get("billing");
    if (billing !== "success") return;
    window.history.replaceState({}, "", window.location.pathname);
    let attempts = 0;
    const poll = setInterval(() => {
      attempts += 1;
      refreshSubscription();
      if (attempts >= 5) clearInterval(poll);
    }, 2000);
    return () => clearInterval(poll);
  }, [refreshSubscription]);

  // ── Auth helpers ──────────────────────────────────────────────────────────
  const handleAuthSuccess = useCallback((authUser) => {
    setUser(authUser);
    setAuthView(null);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setAuthView(null);
  }, []);

  // ── Core API ──────────────────────────────────────────────────────────────
  const runScan = useCallback(async () => {
    setLoading(true);
    setScanError(null);
    setScanPhase("Connecting to market data...");
    try {
      setScanPhase("Fetching quotes & candles...");
      const r = await axios.get("/api/scan", { timeout: 180000 });
      const stocks = r.data.stocks || [];
      setScannedStocks(stocks);
      setCoverage(r.data.coverage || null);
      // The server serves the most recent completed full-market scan, so the
      // timestamp is when that scan ran — not when the button was pressed.
      setLastScan(r.data.timestamp ? new Date(r.data.timestamp) : new Date());
      fetchMarket();

      // Check alerts
      const fired = [];
      stocks.forEach((s) => {
        // Rules may be a bare price or the smart-alert object — alertTargets
        // reads both, where parseFloat() silently produced NaN on the latter.
        const hit = alertTargets(alerts[s.symbol]).find((t) => s.price >= t);
        if (hit !== undefined) {
          fired.push({ symbol: s.symbol, price: s.price, target: hit });
        }
      });
      if (fired.length) {
        setFiredAlerts((p) => [...fired.map((f) => `${f.symbol} hit $${f.price.toFixed(2)}`), ...p].slice(0, 8));
        setAlerts((p) => { const n = { ...p }; fired.forEach((f) => delete n[f.symbol]); return n; });
        axios.post("/api/alerts/notify", { fired }).catch(() => { });
      }

      updatePredictions(stocks);
    } catch (e) {
      setScanError(e?.response?.data?.error || "Scan failed — is the server running? (npm run dev in /server)");
    }
    setLoading(false);
    setScanPhase("");
  }, [alerts, setAlerts, fetchMarket, updatePredictions, setScannedStocks, setLastScan]);

  const handleToggleWL = useCallback((symbol) => {
    if (!watchlist.includes(symbol)) trackEvent("watchlist_created", { symbol });
    toggleWL(symbol);
  }, [toggleWL, watchlist]);

  const { autoScan, setAutoScan, autoInterval, setAutoInterval, countdown } = useAutoScan(runScan);

  const fetchNews = useCallback(async (sym) => {
    if (newsCache.current[sym]) return newsCache.current[sym];
    try {
      const r = await axios.get(`/api/news/${sym}`);
      const n = r.data.news || [];
      newsCache.current[sym] = n;
      return n;
    } catch { return []; }
  }, []);

  const handleStock = useCallback(async (stock) => {
    setSelectedStock(stock);
    setLoadingDetail(true);
    const [news, candleRes] = await Promise.allSettled([
      fetchNews(stock.symbol),
      !stock.candleData ? axios.get(`/api/candles/${stock.symbol}`) : Promise.resolve(null),
    ]);
    if (news.status === "fulfilled") setNewsMap((p) => ({ ...p, [stock.symbol]: news.value }));
    if (candleRes.status === "fulfilled" && candleRes.value?.data?.candles) {
      setSelectedStock((prev) => ({ ...prev, _freshCandles: candleRes.value.data.candles }));
    }
    setLoadingDetail(false);
  }, [fetchNews]);

  const searchStock = useCallback(async (symbol) => {
    const res = await axios.get(`/api/scan/lookup/${encodeURIComponent(symbol)}`);
    const stock = res.data.stock;
    setScannedStocks((prev) => [stock, ...prev.filter((s) => s.symbol !== stock.symbol)].sort((a, b) => b.score - a.score));
    handleStock(stock);
  }, [handleStock, setScannedStocks]);

  const exportCSV = useCallback(() => {
    if (!scannedStocks.length) return;
    const rows = [
      ["Symbol", "Name", "Sector", "Price", "Change%", "Score", "Momentum", "Position", "Trend", "5D%", "20D%", "TechnicalBias"],
      ...scannedStocks.map((s) => [s.symbol, s.name, s.sector, s.price, s.change?.toFixed(2), s.score, s.momentum, s.volumeScore, s.trend, s.fiveDayReturn ?? "", s.twentyDayReturn ?? "", s.tomorrowBias]),
    ];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" }));
    a.download = `scan_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  }, [scannedStocks]);

  const handleDismissAlert = useCallback((i) => {
    setFiredAlerts((p) => p.filter((_, j) => j !== i));
  }, []);

  // ── Derived state (memoized) ───────────────────────────────────────────────
  const filtered = useMemo(() =>
    scannedStocks
      .filter((s) => sector === "All" || s.sector === sector || (sector === "Dynamic" && (s.isDynamic || s.isFMP)))
      .filter((s) => !search || s.symbol.toLowerCase().includes(search.toLowerCase()) || s.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => sortBy === "score" ? b.score - a.score : sortBy === "change" ? b.change - a.change : sortBy === "5d" ? (b.fiveDayReturn ?? -999) - (a.fiveDayReturn ?? -999) : a.symbol.localeCompare(b.symbol)),
    [scannedStocks, sector, search, sortBy]
  );

  const topPicks = useMemo(() => scannedStocks.slice(0, topN), [scannedStocks, topN]);
  const projGain = useMemo(() => topPicks.length > 0 ? topPicks.reduce((a, b) => a + (b.change || 0), 0) / topPicks.length : 0, [topPicks]);

  // ── Password reset link ───────────────────────────────────────────────────
  if (resetToken) {
    return <ResetPassword token={resetToken} onDone={handleResetDone} />;
  }

  // ── Session loading screen ────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="ts-scan-shell" style={{ minHeight: "100vh", background: BG, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18 }}>
        <div className="ts-gradient-text" style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: 3 }}>
          Trade Scanner
        </div>
        <div className="ts-loading-dots" aria-label="Loading">
          <i /><i /><i />
        </div>
      </div>
    );
  }

  // ── Auth gate ─────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <>
        <Landing onLogin={() => setAuthView("login")} onRegister={() => setAuthView("register")} />
        {authView && <AuthModal defaultTab={authView} onSuccess={handleAuthSuccess} onClose={() => setAuthView(null)} />}
      </>
    );
  }

  if (!user.emailVerified) {
    return <VerifyEmailGate user={user} onVerified={setUser} onLogout={handleLogout} />;
  }

  if (!user.profileCompleted) {
    return <CompleteProfileGate onComplete={setUser} onLogout={handleLogout} />;
  }

  if (!subLoading && !isPro) {
    return <TrialEndedGate onSubscribe={handleSubscribe} onLogout={handleLogout} />;
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="ts-scan-shell" style={{ minHeight: "100vh", background: BG, color: TX, fontFamily: "'IBM Plex Mono', monospace", position: "relative", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600;700&family=Space+Grotesk:wght@700;800&display=swap');
        html,body,#root{max-width:100%;overflow-x:hidden}
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;min-width:0}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(12px)}to{opacity:1;transform:translateX(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes glowPulse{0%,100%{opacity:0.06}50%{opacity:0.13}}
        @keyframes dotPulse{0%,80%,100%{transform:scale(0.6);opacity:0.3}40%{transform:scale(1.2);opacity:1}}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:${BD};border-radius:4px}
        @media(max-width:768px){.main-layout{padding:10px 10px!important;flex-direction:column!important}.main-layout>div:first-child{min-width:0!important}}
        .hov-row:hover{background:#0d1728!important;cursor:pointer}
        .hov-row{transition:background 0.1s}
        .btn{transition:all 0.12s;cursor:pointer;border:none;font-family:'IBM Plex Mono',monospace}
        .btn:hover{filter:brightness(1.12);transform:translateY(-1px)}
        .btn:active{transform:translateY(0)}
        .card-h{transition:transform 0.15s;cursor:pointer}
        .card-h:hover{transform:translateY(-2px)}
        .star{transition:transform 0.12s;cursor:pointer;user-select:none}
        .star:hover{transform:scale(1.3)}
        input,select{font-family:'IBM Plex Mono',monospace;outline:none}
        input:focus{border-color:${GR}80!important}
        .shimmer{background:linear-gradient(90deg,transparent,${GR}15,transparent);background-size:200%;animation:shimmer 2s infinite}
      `}</style>

      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden", background: "linear-gradient(180deg,rgba(0,230,118,0.035),transparent 28%,rgba(56,189,248,0.035))" }} />

      <div style={{ position: "relative", zIndex: 1 }}>
        <Navbar
          marketOpen={marketOpen}
          tab={tab}
          setTab={setTab}
          watchlistCount={watchlist.length}
          autoScan={autoScan}
          countdown={countdown}
          firedAlerts={firedAlerts}
          setFiredAlerts={setFiredAlerts}
          settingsOpen={settingsOpen}
          setSettingsOpen={setSettingsOpen}
          user={user}
          isPro={isPro}
          subLoading={subLoading}
        />

        <MarketBar marketData={marketData} marketOpen={marketOpen} />

        <SettingsDrawer
          settingsOpen={settingsOpen}
          setSettingsOpen={setSettingsOpen}
          user={user}
          topN={topN} setTopN={setTopN}
          sortBy={sortBy} setSortBy={setSortBy}
          autoScan={autoScan} setAutoScan={setAutoScan}
          autoInterval={autoInterval} setAutoInterval={setAutoInterval}
          onLogout={handleLogout}
          onExportCSV={exportCSV}
          isPro={isPro}
          subscription={subscription}
          paymentUrl=""
          onSubscribe={isPro ? handleManageBilling : handleSubscribe}
          onActivateDemo={() => {}}
          onClearSubscription={() => {}}
          predLog={predLog}
        />

        <div className="main-layout" style={{ display: "flex", maxWidth: 1520, margin: "0 auto", padding: "14px 16px", gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <AlertsBanner firedAlerts={firedAlerts} onDismiss={handleDismissAlert} />

            {isPro && daysLeftInTrial > 0 && daysLeftInTrial <= 7 && (
              <div style={{ background: YL + "10", border: `1px solid ${YL}30`, borderRadius: 6, padding: "6px 12px", marginBottom: 6, fontSize: 9, color: YL, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <span>{daysLeftInTrial === 1 ? "Your trial ends tomorrow" : `${daysLeftInTrial} days left in your trial`} — subscribe to keep access.</span>
                <span style={{ cursor: "pointer", textDecoration: "underline", flexShrink: 0 }} onClick={handleSubscribe}>
                  Subscribe — {pricing.effectiveLabel}/mo{pricing.launchActive ? ` (was ${pricing.listLabel})` : ""}
                </span>
              </div>
            )}

            {syncFailed && (
              <div style={{ background: RD + "10", border: `1px solid ${RD}30`, borderRadius: 6, padding: "6px 12px", marginBottom: 6, fontSize: 9, color: RD, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <span>Couldn't save your latest changes — check your connection.</span>
                <span style={{ cursor: "pointer", textDecoration: "underline", flexShrink: 0 }} onClick={retrySync}>Retry</span>
              </div>
            )}

            <AnimatePresence mode="sync">
              {tab === "scanner" ? (
                <motion.div key="scanner" {...tabVariants}>
                  <ScannerTab
                    loading={loading} scanPhase={scanPhase} scanned={scanned}
                    scannedStocks={scannedStocks} lastScan={lastScan} scanError={scanError}
                    topN={topN} topPicks={topPicks} projGain={projGain}
                    watchlist={watchlist} onToggleWL={handleToggleWL}
                    sector={sector} setSector={setSector}
                    search={search} setSearch={setSearch}
                    filtered={filtered}
                    coverage={coverage}
                    onScan={runScan} onStockClick={handleStock}
                    onSearchStock={searchStock}
                    marketOpen={marketOpen}
                    isPro={isPro}
                    subLoading={subLoading}
                    paymentUrl=""
                    onSubscribe={handleSubscribe}
                    onActivateDemo={() => {}}
                  />
                </motion.div>
              ) : (
                <motion.div key="watchlist" {...tabVariants}>
                  <WatchlistTab
                    watchlist={watchlist}
                    investments={investments}
                    scannedStocks={scannedStocks}
                    onToggleWL={handleToggleWL}
                    onStockClick={handleStock}
                    onAddInvestment={addInvestment}
                    onRemoveInvestment={removeInvestment}
                    onCloseTrade={closeTrade}
                    onSetInvestedAmount={setInvestedAmount}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <StockDetailModal
          stock={selectedStock}
          onClose={() => setSelectedStock(null)}
          watchlist={watchlist} onToggleWL={handleToggleWL}
          alerts={alerts} setAlerts={setAlerts}
          alertInput={alertInput} setAlertInput={setAlertInput}
          newsMap={newsMap} loadingDetail={loadingDetail}
          investments={investments}
          onAddInvestment={addInvestment}
          onRemoveInvestment={removeInvestment}
        />
      </div>
    </div>
  );
}
